import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { money } from '../../lib/format.js'
import { errorMessage, useTRPC } from '../../lib/trpc.js'
import { Modal, ModalCloseButton } from '../Modal.js'
import { Button, ErrorState, Field, Input, Select } from '../ui.js'

/** What a line needs from an item, whether it was found or just made. */
export interface CatalogItem {
  id: string
  code: string | null
  name: string
  /** Ex GST, already at the client's tier where there is one. */
  unitPrice: number
  unitCost: number
  taxRateId: string | null
  taxRateName: string | null
  taxRateBasisPoints: number
}

/** An item as the Catalog page has it, for editing. */
export interface EditableCatalogItem {
  id: string
  code: string | null
  name: string
  unitOfMeasure: string
  unitCost: number
  unitPrice: number
  taxRateId: string | null
  type: { id: string; name: string; archivedAt: Date | string | null } | null
  /** Set on an item that came from ServiceM8. */
  sm8Uuid: string | null
}

/**
 * A catalog item, new or being edited.
 *
 * New from the Catalog page and from the end of a billing line's dropdown,
 * where it starts with whatever had been typed on the line; edited from a
 * row on the Catalog page. Prices are ex GST because that is what the
 * catalog stores; the inc figure is shown under the price so nobody has to
 * do the sum.
 */
export function CatalogItemPopup({
  open,
  item,
  initialName = '',
  onClose,
  onCreated,
}: {
  open: boolean
  /** The item to edit. Without one the popup makes a new item. */
  item?: EditableCatalogItem | null
  initialName?: string
  onClose: () => void
  onCreated?: (item: CatalogItem) => void
}) {
  return (
    <Modal open={open} onClose={onClose} labelledBy="catalog-item-title" className="max-w-[460px]">
      {open ? (
        <Body key={item?.id ?? 'new'} item={item ?? null} initialName={initialName} onClose={onClose} onCreated={onCreated} />
      ) : null}
    </Modal>
  )
}

const dollars = (cents: number) => (cents === 0 ? '' : (cents / 100).toFixed(2))
const cents = (text: string) => (text.trim() === '' ? 0 : Math.round(Number(text) * 100))
const validCents = (value: number) => Number.isFinite(value) && value >= 0

function Body({
  item,
  initialName,
  onClose,
  onCreated,
}: {
  item: EditableCatalogItem | null
  initialName: string
  onClose: () => void
  onCreated: ((item: CatalogItem) => void) | undefined
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()

  const rates = useQuery(trpc.catalog.taxRates.queryOptions())
  const types = useQuery(trpc.catalog.materialTypes.queryOptions({ includeArchived: false }))

  const [name, setName] = useState(item?.name ?? initialName.trim())
  const [code, setCode] = useState(item?.code ?? '')
  const [price, setPrice] = useState(item ? dollars(item.unitPrice) : '')
  const [cost, setCost] = useState(item ? dollars(item.unitCost) : '')
  const [unit, setUnit] = useState(item?.unitOfMeasure ?? 'each')
  const [taxed, setTaxed] = useState(item ? item.taxRateId !== null : true)
  const [typeId, setTypeId] = useState(item?.type?.id ?? '')

  // An item already on another rate keeps it; a new one, or one being given
  // tax for the first time, takes the default.
  const gst = rates.data?.find((rate) => rate.isDefault) ?? null
  const ownRate = item?.taxRateId ? (rates.data?.find((rate) => rate.id === item.taxRateId) ?? null) : null
  const rateOnOffer = ownRate ?? gst
  const taxRate = taxed ? rateOnOffer : null
  // Until the rates load, an edit leaves the item's rate exactly as it was.
  const taxRateId = taxed ? (rateOnOffer?.id ?? item?.taxRateId ?? null) : null

  const sell = cents(price)
  const buy = cents(cost)
  const incTax = validCents(sell) ? Math.round((sell * (10_000 + (taxRate?.rateBasisPoints ?? 0))) / 10_000) : null

  // A retired type is off the list and still on this item. Without it as an
  // option the select would show No type and save that.
  const retired = item?.type?.archivedAt ? item.type : null

  const invalidate = () => void queryClient.invalidateQueries(trpc.catalog.pathFilter())

  const create = useMutation(
    trpc.catalog.createMaterial.mutationOptions({
      onSuccess: ({ id }) => {
        invalidate()
        onCreated?.({
          id,
          code: code.trim() || null,
          name: name.trim(),
          unitPrice: sell,
          unitCost: buy,
          taxRateId,
          taxRateName: taxRate?.name ?? null,
          taxRateBasisPoints: taxRate?.rateBasisPoints ?? 0,
        })
        onClose()
      },
    }),
  )
  const update = useMutation(
    trpc.catalog.updateMaterial.mutationOptions({
      onSuccess: () => {
        invalidate()
        onClose()
      },
    }),
  )
  const saving = item ? update : create

  const ready = name.trim() !== '' && validCents(sell) && validCents(buy)

  const submit = (event: FormEvent) => {
    event.preventDefault()
    // The popup is portalled, but React still bubbles its submit through
    // whatever rendered it.
    event.stopPropagation()
    if (!ready) return

    const fields = {
      name: name.trim(),
      code: code.trim() || null,
      sellPrice: sell,
      costPrice: buy,
      unit: unit.trim() || 'each',
      taxRateId,
      typeId: typeId || null,
    }

    if (item) update.mutate({ id: item.id, ...fields })
    else create.mutate(fields)
  }

  return (
    <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center gap-3 border-b border-ink-200 px-4 py-3">
        <h2 id="catalog-item-title" className="text-base font-semibold text-ink-900">
          {item ? 'Edit catalog item' : 'New catalog item'}
        </h2>
        <div className="ml-auto">
          <ModalCloseButton onClose={onClose} />
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
        {item?.sm8Uuid ? (
          <p className="rounded-md bg-ink-50 px-3 py-2 text-xs text-ink-600 ring-1 ring-ink-200 ring-inset">
            From ServiceM8. If it is changed there, the next sync brings back its name, code, prices and tax.
            The type stays yours.
          </p>
        ) : null}

        <Field label="Name" required htmlFor="catalog-item-name" hint="What the customer reads on the line.">
          <Input id="catalog-item-name" value={name} autoFocus onChange={(event) => setName(event.target.value)} />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Item code" hint="For the office. Not printed." htmlFor="catalog-item-code">
            <Input
              id="catalog-item-code"
              value={code}
              className="font-mono"
              onChange={(event) => setCode(event.target.value)}
            />
          </Field>
          <Field label="Unit" htmlFor="catalog-item-unit">
            <Input id="catalog-item-unit" value={unit} onChange={(event) => setUnit(event.target.value)} />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Price ex GST"
            htmlFor="catalog-item-price"
            {...(validCents(sell)
              ? incTax !== null && taxRate
                ? { hint: `${money(incTax)} inc GST` }
                : {}
              : { error: 'A price in dollars, like 49.50.' })}
          >
            <Input
              id="catalog-item-price"
              inputMode="decimal"
              placeholder="0.00"
              value={price}
              onChange={(event) => setPrice(event.target.value)}
            />
          </Field>
          <Field
            label="Cost ex GST"
            htmlFor="catalog-item-cost"
            {...(validCents(buy) ? { hint: 'What it costs us.' } : { error: 'A cost in dollars, like 12.00.' })}
          >
            <Input
              id="catalog-item-cost"
              inputMode="decimal"
              placeholder="0.00"
              value={cost}
              onChange={(event) => setCost(event.target.value)}
            />
          </Field>
        </div>

        {(types.data && types.data.length > 0) || retired ? (
          <Field label="Type" hint="Optional" htmlFor="catalog-item-type">
            <Select id="catalog-item-type" value={typeId} onChange={(event) => setTypeId(event.target.value)}>
              <option value="">No type</option>
              {(types.data ?? []).map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
              {retired ? <option value={retired.id}>{`${retired.name} (retired)`}</option> : null}
            </Select>
          </Field>
        ) : null}

        {rateOnOffer ? (
          <label className="flex items-center gap-2 text-[13px] text-ink-700">
            <input type="checkbox" checked={taxed} onChange={(event) => setTaxed(event.target.checked)} />
            Charge {rateOnOffer.name}
          </label>
        ) : null}

        {item ? (
          <p className="text-xs text-ink-500">
            Quotes and invoices that already have this item keep the price they were given.
          </p>
        ) : null}

        {saving.error ? <ErrorState message={errorMessage(saving.error)} /> : null}
      </div>

      <div className="flex items-center justify-end gap-2 border-t border-ink-200 px-4 py-3">
        <Button type="button" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" size="sm" variant="primary" disabled={!ready} loading={saving.isPending}>
          {item ? 'Save changes' : onCreated ? 'Add to the catalog and the line' : 'Add to the catalog'}
        </Button>
      </div>
    </form>
  )
}
